import { useMemo, useState, useEffect } from "react";
import {
  Card,
  Title,
  Subtitle,
  Text,
  ProgressBar,
  Select,
  SelectItem,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
} from "@tremor/react";
import { Factory, MousePointerClick } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { TableScroll } from "@/components/ui/TableScroll";
import { incarcareInWindow } from "@/data/derived";
import { getProductivitateOperatori } from "@/data/dataSource";
import { useFilterVersion } from "@/data/filters";
import { useDrilldown } from "@/components/drilldown/Drilldown";
import { formatLunaKey, numar } from "@/lib/format";

function culoareIncarcare(pct: number): "emerald" | "amber" | "rose" | "blue" {
  if (pct >= 80) return "rose";
  if (pct >= 50) return "amber";
  return "blue";
}

export function IncarcareMasiniSection() {
  useFilterVersion();
  const { openDrill } = useDrilldown();
  const masiniToate = incarcareInWindow();
  const operatoriToti = getProductivitateOperatori();

  const luni = useMemo(
    () => Array.from(new Set(masiniToate.map((m) => m.luna_key))).sort(),
    [masiniToate],
  );
  const [lunaSelectata, setLunaSelectata] = useState(luni[luni.length - 1] ?? "");

  // Dacă fereastra de filtrare exclude luna curentă, sari pe ultima disponibilă.
  useEffect(() => {
    if (luni.length && !luni.includes(lunaSelectata)) {
      setLunaSelectata(luni[luni.length - 1]);
    }
  }, [luni, lunaSelectata]);

  const masiniLuna = masiniToate.filter((m) => m.luna_key === lunaSelectata);
  const operatoriLuna = operatoriToti
    .filter((o) => o.luna_key === lunaSelectata)
    .sort((a, b) => a.rank_luna - b.rank_luna);

  return (
    <section>
      <SectionHeader
        id="masini"
        icon={Factory}
        titlu="4. Încărcare mașini vs. capacitate + productivitate operatori"
        descriere="Ex. mașină tăiere 2 m/min × 60 × 7h ≈ 840 m/zi — cât de aproape suntem de capacitatea teoretică?"
      />

      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Text className="text-sm font-medium text-slate-600">Lună:</Text>
        <Select value={lunaSelectata} onValueChange={setLunaSelectata} enableClear={false} className="w-full max-w-[16rem]">
          {luni.map((l) => (
            <SelectItem key={l} value={l}>
              {formatLunaKey(l, true)}
            </SelectItem>
          ))}
        </Select>
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <MousePointerClick className="h-3.5 w-3.5" />
          Click pe o mașină → încărcarea ei lunară.
        </span>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
        {masiniLuna.map((m) => (
          <Card
            key={m.nume_masina}
            onClick={() => openDrill({ type: "masina", valoare: m.nume_masina })}
            className="flex cursor-pointer flex-col ring-slate-200 transition-shadow hover:shadow-md hover:ring-brand-200"
          >
            <p className="min-w-0 truncate text-sm font-medium text-slate-700" title={m.nume_masina}>
              {m.nume_masina}
            </p>
            <p className="tabular mt-1 text-2xl font-semibold tracking-tight text-slate-900">
              {numar(m.incarcare_pct, 1)}%
            </p>
            <ProgressBar value={m.incarcare_pct} color={culoareIncarcare(m.incarcare_pct)} className="mt-3" />
            <p className="tabular mt-2 text-xs leading-relaxed text-slate-400">
              {numar(m.cant_produsa, 1)} / {numar(m.capacitate_luna, 0)} {m.unitate_capacitate}/lună · {m.zile_active} zile active
            </p>
          </Card>
        ))}
      </div>

      <Card className="ring-slate-200">
        <Title>Productivitate operatori</Title>
        <Subtitle>Output €/oră lucrată — clasament pe luna selectată</Subtitle>
        <TableScroll minWidth="48rem">
          <Table className="mt-3">
            <TableHead>
              <TableRow className="border-slate-200">
                <TableHeaderCell className="w-12">#</TableHeaderCell>
                <TableHeaderCell>Operator</TableHeaderCell>
                <TableHeaderCell>Centru</TableHeaderCell>
                <TableHeaderCell className="text-right">Ore lucrate</TableHeaderCell>
                <TableHeaderCell className="text-right">Operații</TableHeaderCell>
                <TableHeaderCell className="text-right">Output €/oră</TableHeaderCell>
                <TableHeaderCell className="text-right">Operații/oră</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operatoriLuna.map((o) => (
                <TableRow key={`${o.nume_operator}-${o.centru_id}`} className="border-slate-100">
                  <TableCell>
                    <Badge size="xs" color={o.rank_luna <= 3 ? "emerald" : "gray"}>{o.rank_luna}</Badge>
                  </TableCell>
                  <TableCell className="font-medium text-slate-800">{o.nume_operator}</TableCell>
                  <TableCell className="text-slate-500">{o.centru_id}</TableCell>
                  <TableCell className="text-right tabular">{numar(o.ore_lucrate, 1)}</TableCell>
                  <TableCell className="text-right tabular">{o.operatii_efectuate}</TableCell>
                  <TableCell className="text-right tabular font-medium text-slate-800">{numar(o.output_eur_pe_ora, 0)} €</TableCell>
                  <TableCell className="text-right tabular">{numar(o.operatii_pe_ora, 1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      </Card>
    </section>
  );
}
